<template>
  <div class="news-detail-container">
    <div class="news-detail-content">
      <div v-if="loading" class="loading">加载中...</div>
      <div v-else-if="newsDetail" class="news-content">
        <div class="content-layout">
          <!-- 左侧新闻内容区域 -->
          <div class="main-content">
            <!-- 第一行：标题行 -->
            <div class="title-section">
              <!-- 左侧时间模块 -->
              <div class="time-module">
                <div class="day">{{ getDay(newsDetail.createTime) }}</div>
                <div class="year-month">{{ getYearMonth(newsDetail.createTime) }}</div>
              </div>

              <!-- 右侧标题和作者 -->
              <div class="title-author">
                <h1 class="article-title">{{ newsDetail.articleTitle || newsDetail.title }}</h1>
                <div class="author">作者：{{ newsDetail.author || '暂无' }}</div>
              </div>
            </div>

            <!-- 第二行：副标题 -->
            <div v-if="newsDetail.subhead" class="subtitle-section">
              {{ newsDetail.subhead }}
            </div>

            <!-- 第三行：分割线 -->
            <div class="divider"></div>

            <!-- 正文内容 -->
            <div class="content-section">
              <div class="news-text" v-html="newsDetail.content">
              </div>
            </div>

            <!-- 正文后的加粗分割线 -->
            <div class="gray-divider"></div>

            <!-- 上一页下一页导航 -->
            <div class="navigation-section">
              <div class="prev-article">
                <span v-if="newsDetail.previousArticle">
                  上一页：
                  <a @click="navigateToArticle(newsDetail.previousArticle)" class="nav-link">
                    {{ newsDetail.previousArticle.articleTitle }}
                  </a>
                </span>
                <span v-else class="nav-disabled">上一页：无</span>
              </div>

              <div class="next-article">
                <span v-if="newsDetail.nextArticle">
                  <a @click="navigateToArticle(newsDetail.nextArticle)" class="nav-link">
                    {{ newsDetail.nextArticle.articleTitle }}
                  </a>
                  ：下一页
                </span>
                <span v-else class="nav-disabled">无：下一页</span>
              </div>
            </div>
          </div>

          <!-- 右侧推荐列表区域 -->
          <div class="sidebar-content">
            <div class="recommendation-section">
              <h3 class="recommendation-title">推荐阅读</h3>
              <div v-if="recommendLoading" class="recommendation-loading">
                加载中...
              </div>
              <div class="recommendation-list" v-else-if="recommendList && recommendList.length">
                <div
                  v-for="article in recommendList"
                  :key="article.articleId || article.id"
                  class="recommendation-item"
                  @click="navigateToArticle(article)"
                >
                  <div class="item-image">
                    <img :src="article.thumbnail" :alt="article.articleTitle" />
                  </div>
                  <div class="item-content">
                    <h4 class="item-title">{{ article.articleTitle }}</h4>
                    <p class="item-subhead" v-if="article.subhead">{{ article.subhead }}</p>
                    <div class="item-time">{{ formatDate(article.createTime) }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="no-recommendations">
                暂无推荐内容
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="error-message">
        <el-alert
          title="新闻不存在"
          description="抱歉，您访问的新闻不存在或已被删除。"
          type="warning"
          show-icon
          :closable="false"
        />
        <div class="error-actions">
          <el-button @click="goBack" type="primary">返回列表</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getNewsDetailApi, getRecommendNewsListApi } from '@/API/news.js'
import { formatDate } from '@/utils/dateUtils.js'
import { navigateToNews } from '@/utils/newsNavigation.js'

export default {
  name: 'NewsDetail',
  props: {
    category: {
      type: String,
      required: true
    },
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      newsDetail: null,
      loading: false,
      recommendList: [], // 推荐新闻列表
      recommendLoading: false, // 推荐列表加载状态
      categoryMap: {
        'enterprise': '企业新闻',
        'industry': '行业新闻',
        'other': '其他新闻',
        'video': '视频专区'
      }
    }
  },
  computed: {
    categoryName() {
      return this.categoryMap[this.category] || '未知分类'
    }
  },
  created() {
    this.loadNewsDetail()
    this.loadRecommendList()
  },
  watch: {
    '$route'() {
      this.loadNewsDetail()
      this.loadRecommendList()
    }
  },
  methods: {
    async loadNewsDetail() {
      this.loading = true
      try {
        // 根据ID获取新闻详情
        const res = await getNewsDetailApi(this.id)

        if (res && res.data) {
          this.newsDetail = res.data

          // 动态设置页面标题和面包屑标题
          const title = this.newsDetail.articleTitle || this.newsDetail.title
          if (title) {
            document.title = `${title} - ${this.categoryName}`
            // 更新路由meta信息，用于面包屑显示
            this.$route.meta.title = title

            // 触发面包屑更新
            this.$nextTick(() => {
              this.$root.$emit('route-title-updated')
            })
          }
        } else {
          this.newsDetail = null
        }
      } catch (error) {
        console.error('获取新闻详情失败：', error)
        this.newsDetail = null
      } finally {
        this.loading = false
      }
    },

    // 获取推荐新闻列表
    async loadRecommendList() {
      this.recommendLoading = true
      try {
        const res = await getRecommendNewsListApi({ limit: 6 })
        if (res && res.data) {
          this.recommendList = res.data
        } else {
          this.recommendList = []
        }
      } catch (error) {
        console.error('获取推荐新闻列表失败：', error)
        this.recommendList = []
      } finally {
        this.recommendLoading = false
      }
    },

    // 获取日期中的天数
    getDay(dateString) {
      if (!dateString) return '--'
      const date = new Date(dateString)
      return date.getDate().toString().padStart(2, '0')
    },

    // 获取年月格式
    getYearMonth(dateString) {
      if (!dateString) return '--'
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      return `${year}-${month}`
    },

    // 导航到文章
    async navigateToArticle(article) {
      if (!article) return

      try {
        // 使用新闻跳转工具方法
        // 根据文章对象判断跳转类型和详情展示
        const categoryPath = article.category || this.category
        await navigateToNews(article, categoryPath, this.$router)
      } catch (error) {
        console.error('导航到文章失败:', error)
        // 如果跳转工具失败，使用备用方案
        const articleId = article.articleId || article.id
        const categoryPath = article.category || this.category

        if (articleId && categoryPath) {
          this.$router.push(`/news/detail/${categoryPath}/${articleId}`)
        }
      }
    },

    // 格式化日期 - 使用公共方法
    formatDate,

    goBack() {
      // 返回对应的新闻分类列表页
      this.$router.push(`/news/${this.category}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.news-detail-container {
  width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 0;
}

.news-detail-content {
  width: 70%;
  //max-width: 1300px;
  margin: 0 auto;
  background: white;
  padding: 40px 20px;

  @media screen and (max-width: 768px) {
    padding: 20px;
  }
}

.loading {
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
}

.news-content {
  width: 100%;
}

// 左右两栏布局
.content-layout {
  display: flex;
  gap: 40px;
  align-items: flex-start;

  @media screen and (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
}

// 左侧主内容区域
.main-content {
  width: 70%;
  min-width: 0; // 防止flex子元素溢出
  overflow: hidden; // 防止内容溢出到右侧
  word-wrap: break-word; // 处理长单词换行
  word-break: break-word; // 处理中文换行
}

// 右侧推荐区域
.sidebar-content {
  width: 30%;
  min-width: 280px;

  @media screen and (max-width: 1024px) {
    min-width: auto;
    width: 100%;
  }
}

// 第一行：标题行
.title-section {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
}

// 左侧时间模块
.time-module {
  width: 80px;
  height: 80px;
  background-color: #0555b6;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  flex-shrink: 0;

  .day {
    font-size: 28px;
    font-weight: bold;
    line-height: 1;
  }

  .year-month {
    font-size: 14px;
    line-height: 1;
    margin-top: 2px;
  }
}

// 右侧标题和作者
.title-author {
  flex: 1;

  .article-title {
    font-size: 28px;
    font-weight: bold;
    color: #333;
    line-height: 1.4;
    margin: 0 0 10px 0;

    @media screen and (max-width: 768px) {
      font-size: 24px;
    }
  }

  .author {
    font-size: 14px;
    color: #666;
  }
}

// 第二行：副标题
.subtitle-section {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;

  // 最多两行显示，溢出省略号
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

// 第三行：分割线
.divider {
  height: 1px;
  background-color: #eee;
  margin-bottom: 30px;
}

// 正文内容
.content-section {
  margin-bottom: 40px;
  width: 100%;
  overflow: hidden; // 防止内容溢出

  .news-text {
    font-size: 16px;
    line-height: 1.8;
    color: #333;
    width: 100%;
    max-width: 100%;
    overflow: hidden; // 防止富文本内容溢出
    word-wrap: break-word;
    word-break: break-word;

    // 处理富文本内容样式
    :deep(p) {
      margin-bottom: 16px;
      text-align: justify;
    }

    // 图片样式 - 确保不超出容器宽度
    :deep(img) {
      max-width: 100%;
      width: auto;
      height: auto;
      margin: 16px 0;
      border-radius: 4px;
      display: block;
      // 防止图片超出父容器
      box-sizing: border-box;
    }

    // 视频样式 - 确保不超出容器宽度
    :deep(video) {
      max-width: 100%;
      width: auto;
      height: auto;
      margin: 16px 0;
      border-radius: 4px;
      display: block;
      // 防止视频超出父容器
      box-sizing: border-box;
    }

    // iframe样式 - 处理嵌入的视频或内容
    :deep(iframe) {
      max-width: 100%;
      width: 100%;
      height: auto;
      min-height: 200px;
      margin: 16px 0;
      border-radius: 4px;
      display: block;
      box-sizing: border-box;
    }

    // 处理可能包含媒体元素的容器
    :deep(.video-container),
    :deep(.image-container),
    :deep(.media-container) {
      max-width: 100%;
      overflow: hidden;
      margin: 16px 0;
      border-radius: 4px;

      img, video, iframe {
        max-width: 100%;
        width: auto;
        height: auto;
        display: block;
      }
    }

    // 处理表格 - 防止表格过宽
    :deep(table) {
      max-width: 100%;
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;
      overflow-x: auto;
      display: block;
      white-space: nowrap;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }

    :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
      margin: 24px 0 16px 0;
      font-weight: bold;
    }

    :deep(ul), :deep(ol) {
      margin: 16px 0;
      padding-left: 24px;
    }

    :deep(blockquote) {
      margin: 16px 0;
      padding: 16px;
      background: #f8f9fa;
      border-left: 4px solid #0555b6;
      border-radius: 4px;
    }

    // 处理任何可能超出宽度的元素
    :deep(*) {
      max-width: 100%;
      box-sizing: border-box;
    }
  }
}

// 正文后的加粗分割线
.gray-divider {
  height: 3px;
  background-color: #ccc;
  margin-bottom: 30px;
}

// 上一页下一页导航
.navigation-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 14px;
  margin-bottom: 40px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .prev-article, .next-article {
    flex: 1;
    max-width: 45%;

    @media screen and (max-width: 768px) {
      text-align: center;
      max-width: 100%;
    }
  }

  .prev-article {
    text-align: left;

    @media screen and (max-width: 768px) {
      text-align: center;
    }
  }

  .next-article {
    text-align: right;

    @media screen and (max-width: 768px) {
      text-align: center;
    }
  }

  .nav-link {
    color: #999;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    max-width: 340px;
    line-height: 1.4;

    // 最多两行显示，溢出省略号
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;

    &:hover {
      color: #0555b6;
      text-decoration: underline;
    }
  }

  .nav-disabled {
    color: #ccc;
    cursor: not-allowed;
  }
}

.error-message {
  padding: 60px 40px;
  text-align: center;

  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }

  .error-actions {
    margin-top: 20px;
  }
}

// 推荐阅读区域
.recommendation-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;

  .recommendation-title {
    font-size: 18px;
    font-weight: bold;
    color: #333;
    margin: 0 0 20px 0;
    padding-bottom: 10px;
    border-bottom: 2px solid #0555b6;
  }

  .recommendation-list {
    max-height: 600px;
    overflow-y: auto;

    // 自定义滚动条样式
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 3px;

      &:hover {
        background: #999;
      }
    }
  }

  .recommendation-item {
    display: flex;
    gap: 12px;
    padding: 15px 0;
    border-bottom: 1px solid #eee;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      .item-image img {
        transform: scale(1.05);
      }
    }

      .item-image {
        flex-shrink: 0;
        width: 100px;
        height: 75px;
        overflow: hidden;
        border-radius: 4px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        transition: transform 0.3s ease;
      }
    }

    .item-content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .item-title {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        line-height: 1.4;
        margin: 0 0 8px 0;
        transition: color 0.3s ease;

        // 单行显示，溢出省略号
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
      }

      .item-subhead {
        font-size: 12px;
        color: #666;
        line-height: 1.4;
        margin: 0 0 8px 0;

        // 最多两行显示，溢出省略号
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
      }

      .item-time {
        font-size: 11px;
        color: #999;
        margin-top: auto;
      }
    }
  }

  .recommendation-loading {
    text-align: center;
    color: #666;
    padding: 40px 20px;
    font-size: 14px;
  }

  .no-recommendations {
    text-align: center;
    color: #999;
    padding: 40px 20px;
    font-size: 14px;
  }
}
</style>
